import { z } from 'zod';

export const DEFAULT_HOMEPAGE_RULES = `この鯖に参加する人は、以下のルールを守ってね。
Discordでもマイクラ鯖の中でも同じルールで運営しているよ。

## Discord
* 暴言や誹謗中傷をしない
* 自分や他人の個人情報を書き込まない
* 運営の許可なく宣伝をしない
* スパムなどの荒らし行為をしない
* 成人向けや過激な内容を投稿しない
* 他人の発言やスクショを晒さない（配信や録画にそのまま映る場合は、下の「配信」のルールに従う）

## マイクラ鯖
* 他人の建築や拠点を壊したり、チェストから盗んだりしない
* 他人の装置やアイテムは、持ち主の許可を得てから使う
* PvPはお互いに同意した人同士だけ
* チートや不正なMOD・許可されていないクライアントは使わない
* 土地の確保は、サーバーの機能でできる範囲ならOK
* 大型の自動化装置などは、サーバーを重くしすぎない範囲ならOK。重すぎると運営が判断したときは、止めたり作り直したりをお願いすることがある

## 配信
* 誰でも自由に配信してOK
* 配信するときは、鯖名（Three S）を概要欄などに書く
* 参加者は、ほかの人の配信に映ったり、ゲーム内ボイスチャットの声が入ったりすることがある。参加した時点でこれを了承したものとする
* ほかの人の配信のネタバレや、配信中の邪魔をしない

## サイトへの表示
* このサイトには、いまサーバーで遊んでいる人のプレイヤー名とスキンの顔が表示される。参加した時点でこれを了承したものとする

## ルールを守らなかった場合
注意 → 警告 → 一時的な参加禁止 → 永久BAN の順に対応します。
悪質な場合は段階を飛ばして対応することがあります。
参加禁止中に別のアカウントで参加した場合も、同じように対応します。

## そのほか
* ルール違反を見つけたら、Discordで運営に知らせてね
* ここに書いていないことは、運営の判断に従ってね
* ルールは変わることがあります。変わったときはDiscordでお知らせします`;

const text = (max: number) => z.string().trim().max(max);

export const HomepageServerDisplaySchema = z
  .object({
    /** 公開ページに出す名前。空ならサーバーの名前 */
    title: text(40).default(''),
    /** カードに出すひとこと（例: クリエイティブ）。空なら Minecraft の版 */
    tagline: text(40).default(''),
    description: text(300).default(''),
    /** 入り方の説明。空なら Hub での /server コマンドなどを自動で出す */
    joinHint: text(120).default(''),
    /** 統合版のアドレスをトップに出すか */
    showBedrock: z.boolean().default(true),
  })
  .strict();
export type HomepageServerDisplay = z.infer<typeof HomepageServerDisplaySchema>;

export const HomepageSettingsSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().max(500),
    discordInviteUrl: z.union([
      z.literal(''),
      z
        .string()
        .url()
        .max(500)
        .refine((value) => new URL(value).protocol === 'https:', 'HTTPS URL が必要です'),
    ]),
    /** ルールのページの本文。「## 見出し」「* 箇条書き」が使える */
    rules: z.string().trim().max(20000).default(DEFAULT_HOMEPAGE_RULES),
    servers: z.record(z.string().min(1).max(64), HomepageServerDisplaySchema).default({}),
  })
  .strict();
export type HomepageSettings = z.infer<typeof HomepageSettingsSchema>;
export type HomepageSettingsInput = z.input<typeof HomepageSettingsSchema>;

/** 背景（hero）か、サーバーごと（server:<id>）の写真。複数あれば順に切り替える */
export const HomepageImageTargetSchema = z.union([
  z.literal('hero'),
  z.string().regex(/^server:[a-z0-9][a-z0-9-]{0,62}$/),
]);
export const HomepageImageSchema = z.object({
  id: z.string(),
  target: HomepageImageTargetSchema,
  contentType: z.enum(['image/png', 'image/jpeg']),
  bytes: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type HomepageImage = z.infer<typeof HomepageImageSchema>;
export const HOMEPAGE_IMAGE_LIMITS = { bytes: 5 * 1024 * 1024, perTarget: 10 } as const;

/** 公開ページが 30 秒ごとに取り直す状態。内部の情報（ノード・IP）は入れない */
export const HomepageStatusSchema = z.object({
  online: z.number().int().nonnegative(),
  running: z.number().int().nonnegative(),
  servers: z.array(
    z.object({
      id: z.string(),
      running: z.boolean(),
      players: z.number().int().nonnegative().nullable(),
      playerNames: z.array(z.string()),
    }),
  ),
});
export type HomepageStatus = z.infer<typeof HomepageStatusSchema>;
